// FieldOptionHelpers.js
// Pure helpers for consuming already-resolved field options.

function hasUsableValue(value) {
  return value !== undefined && value !== null;
}

function getOptionLabel(option) {
  if (!option || typeof option !== "object") return "";

  if (typeof option.name === "string" && option.name.trim() !== "") {
    return option.name;
  }

  if (typeof option.label === "string" && option.label.trim() !== "") {
    return option.label;
  }

  return hasUsableValue(option.value) ? String(option.value) : "";
}

function normalizeLookupArgs(input) {
  if (Array.isArray(input)) {
    return { flatOptions: input, treeOptions: [] };
  }

  return {
    flatOptions: Array.isArray(input?.flatOptions) ? input.flatOptions : [],
    treeOptions: Array.isArray(input?.treeOptions) ? input.treeOptions : [],
  };
}

export function flattenOptionEntries(input = {}) {
  const { flatOptions, treeOptions } = normalizeLookupArgs(input);
  const entries = [];

  const add = (option) => {
    if (!option || typeof option !== "object" || !hasUsableValue(option.value)) return;

    entries.push({
      value: option.value,
      key: String(option.value),
      label: getOptionLabel(option),
      option,
    });
  };

  flatOptions.forEach(add);

  const walk = (nodes = []) => {
    nodes.forEach((node) => {
      add(node);
      if (Array.isArray(node?.children) && node.children.length > 0) {
        walk(node.children);
      }
    });
  };

  walk(treeOptions);

  return entries;
}

export function buildOptionValueSet(input = {}) {
  return new Set(flattenOptionEntries(input).map((entry) => entry.key));
}

export function buildOptionLabelMap(input = {}) {
  const map = new Map();
  flattenOptionEntries(input).forEach((entry) => {
    map.set(entry.key, entry.label || entry.key);
  });
  return map;
}

export function buildOptionLookup(input = {}) {
  const entries = flattenOptionEntries(input);
  const values = new Set();
  const labelMap = new Map();

  entries.forEach((entry) => {
    values.add(entry.key);
    labelMap.set(entry.key, entry.label || entry.key);
  });

  return {
    entries,
    values,
    labelMap,
    hasValue: (value) => values.has(String(value)),
    labelForValue: (value) => labelMap.get(String(value)),
  };
}
